import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useNotificationApi } from "@/api/notifications";
import { useI18n } from "@/locales/i18n-provider";
import {
  decodeApplicationServerKey,
  detachRegisteredWebPushSubscription,
  detachWebPushLocally,
  getExistingWebPushSubscription,
  getReadyWebPushRegistration,
  getWebPushSupport,
  registerAndConfigureWebPushRenewal,
  serializeWebPushSubscription,
  webPushSubscriptionUsesKey,
} from "./web-push";

export type WebPushStatusKind = "loading" | "subscribed" | "unsubscribed" | "denied" | "unsupported" | "server-disabled" | "error";
export type WebPushPendingAction = "enable" | "disable" | null;

type WebPushEnableStage = "permission" | "configuration" | "service-worker" | "subscription" | "server-registration";

export type WebPushState = {
  status: WebPushStatusKind;
  pendingAction: WebPushPendingAction;
  failedAction: Exclude<WebPushPendingAction, null> | null;
  lastCompletedAction: Exclude<WebPushPendingAction, null> | null;
};

export type WebPushEnableOptions = {
  /** The island owns its follow-up feedback; all other callers keep the default toast feedback. */
  feedback?: "silent" | "toast";
};

export type WebPushController = {
  state: WebPushState;
  enable: (options?: WebPushEnableOptions) => Promise<void>;
  disable: () => Promise<void>;
  retryInspection: () => Promise<void>;
  detachForLogout: () => Promise<void>;
};

type WebPushApi = Pick<ReturnType<typeof useNotificationApi>, "deleteWebPushSubscription" | "getWebPushConfig" | "upsertWebPushSubscription">;

function safeWebPushErrorName(error: unknown): string {
  if (error instanceof DOMException || error instanceof Error) {
    return error.name || "Error";
  }
  return "UnknownError";
}

function reportWebPushEnableFailure(stage: WebPushEnableStage, error: unknown): string {
  const errorName = safeWebPushErrorName(error);
  // Never log the error object itself: Axios errors may retain the subscription
  // endpoint and keys in their request configuration.
  console.warn("Web Push enable failed.", { stage, errorName });
  return `${stage}: ${errorName}`;
}

async function detachSubscription(api: WebPushApi): Promise<{ hadSubscription: boolean; serverFailed: boolean }> {
  // Cache revocation, authenticated removal and unsubscribe share the same
  // cross-tab lock, so a queued registration cannot re-enable an explicit disable.
  return detachRegisteredWebPushSubscription((endpoint, renewalToken) => api.deleteWebPushSubscription(endpoint, renewalToken));
}

async function registerSubscriptionWithServer(api: WebPushApi, subscription: PushSubscription, applicationServerKey: string): Promise<void> {
  const registered = await registerAndConfigureWebPushRenewal(subscription, applicationServerKey, async () => {
    const result = await api.upsertWebPushSubscription(serializeWebPushSubscription(subscription));
    return result.renewalToken;
  });
  if (!registered) {
    throw new Error("The Web Push subscription was removed before registration completed.");
  }
}

async function replaceSubscriptionForKeyChange(
  api: WebPushApi,
  subscription: PushSubscription,
  applicationServerKey: Uint8Array<ArrayBuffer>,
): Promise<PushSubscription> {
  if (webPushSubscriptionUsesKey(subscription, applicationServerKey)) {
    return subscription;
  }

  // The endpoint must be detached before the local subscription is replaced. A failed
  // server call must never prevent local cleanup; stale endpoints are pruned by delivery.
  await detachSubscription(api);
  const registration = await getReadyWebPushRegistration();
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
}

async function inspectAndSyncExistingSubscription(api: WebPushApi): Promise<WebPushStatusKind> {
  if (!getWebPushSupport().supported) {
    return "unsupported";
  }

  const config = await api.getWebPushConfig();
  if (!config.enabled || !config.applicationServerKey) {
    return "server-disabled";
  }
  if (Notification.permission === "denied") {
    return "denied";
  }

  const existingSubscription = await getExistingWebPushSubscription();
  if (!existingSubscription) {
    return "unsubscribed";
  }

  const applicationServerKey = decodeApplicationServerKey(config.applicationServerKey);
  const subscription = await replaceSubscriptionForKeyChange(api, existingSubscription, applicationServerKey);
  await registerSubscriptionWithServer(api, subscription, config.applicationServerKey);
  return "subscribed";
}

export function useWebPush(agentId: string): WebPushController {
  const api = useNotificationApi();
  const { t } = useI18n();
  const [state, setState] = useState<WebPushState>({
    status: "loading",
    pendingAction: null,
    failedAction: null,
    lastCompletedAction: null,
  });
  const initializationRef = useRef<{ agentId: string; promise: Promise<WebPushStatusKind> } | null>(null);
  const operationRef = useRef<Promise<void> | null>(null);
  const operationQueueRef = useRef<Promise<void>>(Promise.resolve());

  const enqueueOperation = useCallback(<T>(operation: () => Promise<T>): Promise<T> => {
    const result = operationQueueRef.current.then(operation, operation);
    operationQueueRef.current = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }, []);

  const trackOperation = useCallback((operation: Promise<void>): Promise<void> => {
    operationRef.current = operation;
    void operation.then(
      () => {
        if (operationRef.current === operation) {
          operationRef.current = null;
        }
      },
      () => {
        if (operationRef.current === operation) {
          operationRef.current = null;
        }
      },
    );
    return operation;
  }, []);

  useEffect(() => {
    let active = true;
    let initialization = initializationRef.current;
    if (!initialization || initialization.agentId !== agentId) {
      const previousAgentId = initialization?.agentId;
      initialization = {
        agentId,
        promise: enqueueOperation(async () => {
          if (previousAgentId && previousAgentId !== agentId) {
            await detachRegisteredWebPushSubscription();
          }
          return inspectAndSyncExistingSubscription(api);
        }),
      };
      initializationRef.current = initialization;
      trackOperation(
        initialization.promise.then(
          () => undefined,
          () => undefined,
        ),
      );
    }

    setState({ status: "loading", pendingAction: null, failedAction: null, lastCompletedAction: null });
    void initialization.promise.then(
      (status) => {
        if (active) {
          setState({ status, pendingAction: null, failedAction: null, lastCompletedAction: null });
        }
      },
      () => {
        if (active) {
          setState({ status: "error", pendingAction: null, failedAction: null, lastCompletedAction: null });
        }
      },
    );

    return () => {
      active = false;
    };
  }, [agentId, api, enqueueOperation, trackOperation]);

  const retryInspection = useCallback(() => {
    if (operationRef.current) {
      return operationRef.current;
    }

    const operation = enqueueOperation(async () => {
      setState({ status: "loading", pendingAction: null, failedAction: null, lastCompletedAction: null });
      try {
        const status = await inspectAndSyncExistingSubscription(api);
        setState({ status, pendingAction: null, failedAction: null, lastCompletedAction: null });
      } catch {
        setState({ status: "error", pendingAction: null, failedAction: null, lastCompletedAction: null });
      }
    });

    return trackOperation(operation);
  }, [api, enqueueOperation, trackOperation]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const handleSubscriptionChange = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (data && typeof data === "object" && "type" in data && data.type === "TICKBIRD_WEB_PUSH_SUBSCRIPTION_CHANGED") {
        void retryInspection();
      }
    };

    navigator.serviceWorker.addEventListener("message", handleSubscriptionChange);
    return () => navigator.serviceWorker.removeEventListener("message", handleSubscriptionChange);
  }, [retryInspection]);

  const enable = useCallback(
    (options?: WebPushEnableOptions) => {
      if (operationRef.current) {
        return operationRef.current;
      }

      const support = getWebPushSupport();
      setState((current) => ({ ...current, pendingAction: "enable", failedAction: null, lastCompletedAction: null }));

      // Invoke requestPermission directly from the click callback. Scheduling it
      // through enqueueOperation first loses the transient user activation in Edge,
      // which can leave the permission promise pending without displaying a prompt.
      const permissionRequest = support.supported
        ? Notification.permission === "default"
          ? Notification.requestPermission()
          : Promise.resolve(Notification.permission)
        : Promise.resolve<NotificationPermission>("default");

      const operation = enqueueOperation(async () => {
        let stage: WebPushEnableStage = "permission";

        try {
          if (!support.supported) {
            setState({ status: "unsupported", pendingAction: null, failedAction: null, lastCompletedAction: null });
            return;
          }

          const permission = await permissionRequest;
          if (permission !== "granted") {
            setState({
              status: permission === "denied" ? "denied" : "unsubscribed",
              pendingAction: null,
              failedAction: null,
              lastCompletedAction: null,
            });
            return;
          }

          // Re-read the server config after permission is granted so key rotation and a
          // concurrently disabled channel are still handled correctly.
          stage = "configuration";
          const config = await api.getWebPushConfig();
          if (!config.enabled || !config.applicationServerKey) {
            setState({ status: "server-disabled", pendingAction: null, failedAction: null, lastCompletedAction: null });
            return;
          }

          const applicationServerKey = decodeApplicationServerKey(config.applicationServerKey);
          stage = "service-worker";
          const existingSubscription = await getExistingWebPushSubscription();
          stage = "subscription";
          const subscription = existingSubscription
            ? await replaceSubscriptionForKeyChange(api, existingSubscription, applicationServerKey)
            : await (await getReadyWebPushRegistration()).pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });

          stage = "server-registration";
          await registerSubscriptionWithServer(api, subscription, config.applicationServerKey);
          setState({ status: "subscribed", pendingAction: null, failedAction: null, lastCompletedAction: "enable" });
          if (options?.feedback !== "silent") {
            toast.success(t("notifications.push.enabledSuccess"));
          }
        } catch (error) {
          const failureDetail = reportWebPushEnableFailure(stage, error);
          setState({ status: "error", pendingAction: null, failedAction: "enable", lastCompletedAction: null });
          if (options?.feedback !== "silent") {
            toast.error(`${t("notifications.push.updateFailed")} (${failureDetail})`);
          }
        }
      });

      return trackOperation(operation);
    },
    [api, enqueueOperation, t, trackOperation],
  );

  const disable = useCallback(() => {
    if (operationRef.current) {
      return operationRef.current;
    }

    const operation = enqueueOperation(async () => {
      setState((current) => ({ ...current, pendingAction: "disable", failedAction: null, lastCompletedAction: null }));

      try {
        const { hadSubscription, serverFailed } = await detachSubscription(api);
        if (!hadSubscription && !serverFailed) {
          setState({ status: "unsubscribed", pendingAction: null, failedAction: null, lastCompletedAction: "disable" });
          return;
        }

        setState({ status: "unsubscribed", pendingAction: null, failedAction: null, lastCompletedAction: "disable" });
        if (serverFailed) {
          toast.warning(t("notifications.push.disabledLocalOnly"));
        } else {
          toast.success(t("notifications.push.disabledSuccess"));
        }
      } catch {
        setState({ status: "error", pendingAction: null, failedAction: "disable", lastCompletedAction: null });
        toast.error(t("notifications.push.updateFailed"));
      }
    });

    return trackOperation(operation);
  }, [api, enqueueOperation, t, trackOperation]);

  const detachForLogout = useCallback(() => {
    const operation = enqueueOperation(async () => {
      try {
        await detachRegisteredWebPushSubscription((endpoint, renewalToken) => api.deleteWebPushSubscription(endpoint, renewalToken));
      } catch {
        await detachWebPushLocally();
      } finally {
        setState({ status: "unsubscribed", pendingAction: null, failedAction: null, lastCompletedAction: null });
      }
    });

    return trackOperation(operation);
  }, [api, enqueueOperation, trackOperation]);

  return useMemo(
    () => ({
      state,
      enable,
      disable,
      retryInspection,
      detachForLogout,
    }),
    [detachForLogout, disable, enable, retryInspection, state],
  );
}
